// PURPOSE: Select browser session persistence and renew OIDC access tokens.
// CONTRACT: Refresh requires server opt-in and available persistent storage; logout clears both application user stores.
// DEPENDS: oidc-client-ts PKCE and token revocation, browser storage, public AuthConfig.
// CEILING: Provider authenticates; current API membership authorizes; no credentials outside OIDC storage.
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import type { AuthConfig } from './types';
const pendingKey = 'velox.signin-persistence';
const sessionKey = 'velox.session-persistence';
function persistentStorage(): Storage | null {
  try {
    const key = 'velox.storage-probe'; localStorage.setItem(key, '1'); localStorage.removeItem(key);
    return localStorage;
  } catch { return null; }
}
export interface AuthAdapter { getExpiresAt?(): Promise<number | null>; renew?(): Promise<void>; getEmail?(): Promise<string | null>; getToken(): Promise<string | null>; signIn(): Promise<void>; signOut(): Promise<void>; completeSignIn(): Promise<void> }
export function oidcAdapter(config: AuthConfig): AuthAdapter {
  if (!config.issuer || !config.client_id) throw new Error('Sign-in is not configured yet. An administrator must configure the identity provider.');
  const persistent = persistentStorage();
  const scope = (config.scope || 'openid profile email').split(/\s+/).filter(value => value && value !== 'offline_access');
  const canRemember = (choice: boolean) => !!(choice && config.refresh_enabled && persistent);
  const makeManager = (remember: boolean) => new UserManager({
    authority: config.issuer!, client_id: config.client_id!,
    redirect_uri: config.redirect_uri || `${location.origin}/auth/callback`, post_logout_redirect_uri: `${location.origin}/`,
    response_type: 'code', scope: [...scope, ...(remember ? ['offline_access'] : [])].join(' '),
    automaticSilentRenew: false, revokeTokensOnSignout: false,
    userStore: new WebStorageStateStore({ store: remember ? persistent! : sessionStorage }),
    stateStore: new WebStorageStateStore({ store: sessionStorage }),
  });
  const pending = sessionStorage.getItem(pendingKey) ?? sessionStorage.getItem(sessionKey);
  let remember = canRemember(pending === null || pending === 'true');
  let manager = makeManager(remember);
  let renewalStarted = false;
  let renewal: Promise<User | null> | undefined;
  return {
    getExpiresAt: async () => (await manager.getUser())?.expires_at ?? null,
    renew: async () => {
      renewal ||= manager.signinSilent().finally(() => { renewal = undefined; });
      const user = await renewal;
      if (!user || user.expired) throw new Error('Could not renew.');
    },
    getEmail: async () => (await manager.getUser())?.profile.email || null,
    getToken: async () => {
      let user = await manager.getUser();
      if (user?.expired && remember && user.refresh_token) {
        // Bootstrap after a browser restart may precede the library's renewal timer.
        renewal ||= manager.signinSilent().catch(async () => { await manager.removeUser(); return null; }).finally(() => { renewal = undefined; });
        user = await renewal;
      }
      // Start timers after bootstrap renewal so a restored expired user cannot
      // trigger both the timer and the initial request with the same refresh token.
      if (!renewalStarted) { renewalStarted = true; manager.startSilentRenew(); }
      return user && !user.expired ? user.access_token : null;
    },
    signIn: async () => {
      manager.stopSilentRenew();
      const selected = canRemember(true);
      // Carry the storage actually available at sign-in through this tab's callback.
      sessionStorage.setItem(pendingKey, String(selected));
      // Do not leave a previously remembered user behind when refresh is unavailable.
      if (!selected && persistent) await new WebStorageStateStore({ store: persistent }).remove(`user:${config.issuer}:${config.client_id}`);
      if (selected !== remember) { remember = selected; manager = makeManager(remember); }
      await manager.signinRedirect({ nonce: crypto.randomUUID(), state: { returnTo: location.pathname + location.search } });
    },
    signOut: async () => {
      manager.stopSilentRenew();
      if (renewal) await renewal;
      const other = persistent ? makeManager(!remember) : null;
      let idToken: string | undefined; let failed = false;
      for (const current of [manager, other]) {
        if (!current) continue;
        current.stopSilentRenew();
        try { idToken ||= (await current.getUser())?.id_token; }
        catch { failed = true; }
        for (const type of ['access_token', 'refresh_token'] as const) {
          try { await current.revokeTokens([type]); } catch { failed = true; }
        }
        try { await current.removeUser(); } catch { failed = true; }
      }
      sessionStorage.removeItem(pendingKey);
      sessionStorage.removeItem(sessionKey);
      if (failed) throw new Error('Sign-out could not finish. Token revocation or local cleanup failed; retry sign-out.');
      await manager.signoutRedirect({ id_token_hint: idToken });
    },
    completeSignIn: async () => {
      const user = await manager.signinRedirectCallback();
      sessionStorage.setItem(sessionKey, String(remember)); sessionStorage.removeItem(pendingKey);
      const path = (user.state as { returnTo?: string } | undefined)?.returnTo;
      history.replaceState(null, '', path?.startsWith('/') && !path.startsWith('//') ? path : '/');
    },
  };
}
